import React, { useState } from "react"
import { useNavigate, useParams } from "react-router-dom"
import moment from "moment"
import { useToast } from "~/utils/Toast"

import PageTitle from "~/library/PageTitle"
import LoadingWrapper from "~/library/LoadingWrapper"
import CatchCard from "~/library/CatchCard"
import CatchDropdown from "~/library/CatchDropdown"
import ConfirmationModal from "~/library/ConfirmationModal"

import getAppConfig from "~/utils/getAppConfig"
import useFetchResource from "~/hooks/useFetchResource"
import { apiCategories, apiLineItems } from "~/routes"
import Category from "~/types/Category"
import LineItem from "~/types/LineItem"
import { Link } from "react-router-dom"
import CatchTable from "~/library/CatchTable"
import { ArrowLeft } from "lucide-react"
import { formatCurrency, parseRouteId } from "~/utils/helpers"
import NavigationLink from "~/library/NavigationLink"
import {
  getCategoriesBackPath,
  getCategoriesBackTitle,
  clearCategoriesNavigationState,
} from "~/utils/navigationState"

const CategoryView = () => {
  const [isDeleting, setIsDeleting] = useState(false)
  const navigate = useNavigate()
  const { venueSlug } = getAppConfig()
  const { id } = useParams<{ id: string }>()

  const endpoint = apiCategories.show.path({ venueSlug, id: id! })
  const { data: category, isLoading } = useFetchResource<{ category: Category }, "category">(
    endpoint,
    "category",
    {}
  )

  const { data: lineItems, isLoading: lineItemsLoading } = useFetchResource<
    { lineItems: LineItem[] },
    "lineItems"
  >(
    apiLineItems.index.path({ venueSlug }),
    "lineItems",
    id ? { "q[category_id_eq]": parseRouteId(id) } : {}
  )

  const toast = useToast()

  const renderDetail = (title: string, content: React.ReactNode, size = 6) => {
    return (
      <div className={`col-${size || 6}`}>
        <p className="mt-2 mb-1 text-muted fw-bold font-12 text-uppercase">{title}</p>
        <div className="d-flex">
          <div className="mt-1 font-14 mb-2">{content}</div>
        </div>
      </div>
    )
  }

  const deleteCategory = () => {
    setIsDeleting(true)
    const req = apiCategories.destroy({ data: { venueSlug, id: id! } })
    req
      .then(() => {
        toast("Category deleted successfully", "success")
        clearCategoriesNavigationState()
        navigate("/categories")
      })
      .catch((res) => {
        toast(
          `Error deleting category. ${res.body?.errors?.join(", ") || "Please try again"}`,
          "danger"
        )
        setIsDeleting(false)
      })
  }

  return (
    <LoadingWrapper isLoading={isLoading}>
      <PageTitle
        title={
          <div className="d-flex align-items-center">
            <button
              type="button"
              className="btn btn-link p-0 me-2"
              onClick={() => navigate(getCategoriesBackPath())}
              aria-label={`Go back to ${getCategoriesBackTitle()}`}
              title={`Go back to ${getCategoriesBackTitle()}`}
            >
              <ArrowLeft />
            </button>
            <span>{category.name}</span>
          </div>
        }
      >
        <CatchDropdown
          label="Actions"
          items={[
            {
              label: "Edit Category",
              to: `/categories/${id}/edit`,
            },
            {
              label: "Delete Category",
              className: "text-danger",
              onClick: () => setIsDeleting(true),
            },
          ]}
        />
      </PageTitle>

      <div className="row">
        <div className="col-8">
          <CatchCard title="Category Details">
            <div className="row">
              {renderDetail("Name", category.name, 4)}
              {renderDetail("Line Items Count", category.lineItemsCount, 4)}
              {renderDetail(
                "Status",
                <span
                  className={`badge badge-lg ${category.active ? "bg-success" : "bg-secondary"}`}
                >
                  {category.active ? "Active" : "Inactive"}
                </span>,
                4
              )}
            </div>
          </CatchCard>

          <CatchCard title="Line Items List">
            {lineItemsLoading ? (
              <div className="text-center py-3">
                <div className="spinner-border spinner-border-sm" role="status">
                  <span className="visually-hidden">Loading...</span>
                </div>
                <span className="ms-2">Loading line items...</span>
              </div>
            ) : Array.isArray(lineItems) && lineItems.length > 0 ? (
              <CatchTable
                data={lineItems}
                emptyMessage="No line items found"
                columns={[
                  {
                    key: "name",
                    header: "Name",
                    formatter: (name, data) => (
                      <NavigationLink type="line-items" to={`/line-items/${data.id}`}>
                        {name}
                      </NavigationLink>
                    ),
                  },
                  {
                    key: "amountCents",
                    header: "Amount",
                    formatter: (amountCents) =>
                      formatCurrency({ cents: amountCents, currency_iso: "USD" }),
                  },
                  {
                    key: "minOrderQty",
                    header: "Min Order Qty",
                    formatter: (minOrderQty) => minOrderQty || "—",
                  },
                  {
                    key: "active",
                    header: "Status",
                    formatter: (active) => (
                      <span className={`badge badge-lg ${active ? "bg-success" : "bg-secondary"}`}>
                        {active ? "Active" : "Inactive"}
                      </span>
                    ),
                  },
                ]}
              />
            ) : (
              <div className="text-center py-4">
                <p className="text-muted mb-2">No line items are using this category yet.</p>
                <Link to="/line-items/new" className="btn btn-sm btn-outline-primary">
                  Create First Line Item
                </Link>
              </div>
            )}
          </CatchCard>
        </div>

        <div className="col-4">
          <CatchCard title="Internal Details">
            <div className="row">
              {renderDetail(
                "Created on",
                moment(category?.createdAt).format("MMM Do YYYY hh:mma"),
                12
              )}
              {category?.updatedAt &&
                renderDetail(
                  "Updated on",
                  moment(category?.updatedAt).format("MMM Do YYYY hh:mma"),
                  12
                )}
            </div>
          </CatchCard>
        </div>
      </div>

      <ConfirmationModal
        isOpen={isDeleting}
        onClose={() => setIsDeleting(false)}
        onConfirm={deleteCategory}
        title={`Delete Category "${category?.name}"?`}
        description="This action cannot be undone. Line items in this category will be uncategorized. Are you sure you want to delete this category?"
      />
    </LoadingWrapper>
  )
}

export default CategoryView
